import { AxiosInstance, AxiosResponse, CancelToken } from 'axios'
import { SecurityError } from 'common/src/errors'
import { ValidAPIResponse } from 'common/src/api/websiteFrontendVsWebsiteBackend'
import { ApiMethod } from 'common/src/api/types'
import { isEnumMember } from 'common/src/utils/isEnumMember'
import { frontendLogout } from '@/auth/frontendLogout'
import {
    REQUEST_HEADER,
    requestIDFactory,
} from 'common/src/AsyncStorageMiddleware/util'
import { buildFrontendLogger } from '@/logging/FrontendLogger'
import { getPrettyTimeElapsedSince } from 'common/src/decorators/trace/getPrettyTimeElapsedSince'
import { buildHttpClient } from '@/api/buildHttpClient'
import { attemptWbSessionRefresh } from '@/api/wb/wbSessionRefresh'
import { inspect } from 'util'

const clientSideHttpClient = buildHttpClient()

export const TIMEOUT_IN_MS = 1_000 * 60 * 10 // 10 minutes

const callBackendLogger = buildFrontendLogger('callBackend')
export const callBackend = async <Req, Res>(
    url: string,
    method: ApiMethod,
    data: Req = null,
    cancelToken: CancelToken = null,
    numRetries = 3,
    httpClient: AxiosInstance = clientSideHttpClient
): Promise<AxiosResponse<ValidAPIResponse<Res>>> => {
    const requestID = requestIDFactory()
    const headers: Record<string, string | undefined> = {
        [REQUEST_HEADER]: requestID,
    }
    // The shared client defaults Content-Type to application/json, which
    // clobbers multipart bodies: clearing it per-request lets the browser set
    // multipart/form-data with its boundary for FormData payloads
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
        headers['Content-Type'] = undefined
    }
    const startTime = new Date()

    callBackendLogger.info(
        `=> ${url} / method: ${method} / request ID: ${requestID} / data: ${JSON.stringify(
            data
        )}`,
        { requestID }
    )

    let error: unknown = undefined
    let shouldFireDiscordError = false

    for (let retryCt = 1; retryCt <= numRetries; retryCt++) {
        let response: AxiosResponse<ValidAPIResponse<Res>>
        try {
            switch (method) {
                case ApiMethod.POST:
                    response = await httpClient.post(url, data ?? {}, {
                        cancelToken,
                        headers,
                        timeout: TIMEOUT_IN_MS,
                    })
                    break
                case ApiMethod.GET:
                    response = await httpClient.get(url, {
                        params: data,
                        cancelToken,
                        headers,
                        timeout: TIMEOUT_IN_MS,
                    })
                    break
                default:
                    callBackendLogger.error(
                        `Unexpected request method: ${method}`,
                        { requestID }
                    )
            }
            if (response.data?.success) {
                callBackendLogger.info(
                    `<= ${url} / request ID: ${requestID} / response payload: ${JSON.stringify(
                        response.data.payload
                    )} ${getPrettyTimeElapsedSince(startTime)}`,
                    { requestID }
                )
                return response
            }
            error = response.data.error
        } catch (exception: unknown) {
            error = exception
        }

        shouldFireDiscordError = getShouldReportError({
            errorAsString: error?.toString(),
            url,
            requestID,
        })

        if (retryCt < numRetries) {
            callBackendLogger.info(
                `${url} / request ID: ${requestID} failed with error code ${
                    typeof error === 'object' && 'code' in error
                        ? error.code
                        : 'NO CODE'
                } / error: ${error}. Retry num ${retryCt + 1}...`,
                { requestID }
            )
        } else {
            if (shouldFireDiscordError) {
                callBackendLogger.error(
                    `${url} / request ID ${requestID} failed with error ${inspect(
                        error
                    )}. Not retrying, just returning...`,
                    { requestID }
                )
            }
            break
        }
    }

    if (
        url.startsWith('/wb/') &&
        isEnumMember(SecurityError, error?.toString())
    ) {
        callBackendLogger.info(
            `WB auth error on ${url}, attempting session refresh...`,
            { requestID }
        )
        const refreshed = await attemptWbSessionRefresh()
        if (refreshed) {
            callBackendLogger.info(`WB session refreshed, retrying ${url}...`, {
                requestID,
            })
            return callBackend(url, method, data, cancelToken, 1, httpClient)
        }
        callBackendLogger.warn(
            `WB session refresh failed for ${url}, returning error.`,
            { requestID }
        )
    }

    if (shouldFireDiscordError) {
        await handleRequestFailure(error, url, requestID, startTime)
    }
    return {
        data: {
            success: false,
            isError: true,
            payload: null,
            error: error ? error.toString() : null,
        },
        status: null,
        headers: null,
        statusText: null,
        config: null,
    }
}

const getShouldReportError = (input: {
    errorAsString: string
    url: string
    requestID: string
}): boolean => {
    const { errorAsString, url, requestID } = input
    if (isEnumMember(SecurityError, errorAsString)) {
        return false
    }

    if (
        errorAsString.includes('AxiosError: Network Error') &&
        url === '/wb/studyTime/getCurrentStudyTimeStatus'
    ) {
        callBackendLogger.info(
            `Not firing discord msg for getCurrentStudyTimeStatus / networkError / requestID: ${requestID}`,
            { requestID }
        )
        return false
    }

    return true
}

const handleRequestFailure = async (
    error: unknown,
    url: string,
    requestID: string,
    startTime: Date
): Promise<void> => {
    const errorAsString = error?.toString()
    callBackendLogger.error(
        `<!= ${url} / request ID ${requestID} failed with error code ${errorAsString} ${getPrettyTimeElapsedSince(
            startTime
        )}`,
        { requestID }
    )
    if (isEnumMember(SecurityError, errorAsString)) {
        callBackendLogger.error(
            `Detected a security error: ${error}. Immediately logging user out.`,
            { requestID }
        )
        await frontendLogout()
    }
}
